import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { X, Sparkles, Brain, Loader2, AlertCircle, Wand2, Upload } from "lucide-react";
import LocationSelector from "./LocationSelector";
import ExerciseScenarioEditor from "./ExerciseScenarioEditor";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import { CommanServices } from '@/services/commanServices';
import {
  buildIncidentDefinitionChanges,
  canCorrectRealIncidentReport,
  realIncidentCorrectionLockReason,
} from '../utils/incidentCorrectionPolicy';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";


const TimelineEntryModal = ({ incident, onClose, onSuccess, activeGroup = null, editItem = null, isAdmin = false }) => {
  const [formData, setFormData] = useState({
    group: activeGroup || '',
    title: '',
    details: '',
    chips: '',
    actor_name: '',
    actor_type: 'user',
    // Report & Detection specific fields
    reported_at: '',
    reported_by: '',
    source_origin: '',
    source_method: '',
    incident_type: '',
    event: '',
    trigger: '',
    location_override: false,
    location_path: [],
    location_path_id: '', // Store the ID separately for LocationSelector
    initial_description: '',
    reporter_contact: '',
    attachments: [],
    // Other group-specific fields
    verification_status: 'Unverified',
    eta_duration: '',
    notification_mode: [],
    escalation_rule: '',
    escalation_level: '1',
    action_type: '',
    result_outcome: '',
    risk_level_change: 'No Change',
    impacted_areas: [],
    resolution_status: '',
    follow_up_required: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [originalData, setOriginalData] = useState(null); // Track original values for change detection
  const [aiLoaderDescription, setAiLoaderDescription] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [showAiConfirm, setShowAiConfirm] = useState(false);
  const [pendingAiText, setPendingAiText] = useState('');
  const [previewAttachment, setPreviewAttachment] = useState(null); // For attachment preview modal
  const [showCorrectionConfirm, setShowCorrectionConfirm] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [pendingCorrectionChanges, setPendingCorrectionChanges] = useState([]);

  // --- Report editing (classification + location) ---
  // Exercises: editable while at the Requested stage (rebuilds the plan).
  // Real incidents: admins can correct the reported classification/location
  // only before operations or response-plan activation; the backend re-runs
  // scenario matching and records the confirmed correction.
  const incidentStatusLc = String(incident?.status || '').toLowerCase();
  const isRealIncident = !incident?.is_exercise;
  const correctionLockReason = isRealIncident ? realIncidentCorrectionLockReason(incident) : '';
  const canCorrectIncidentDefinition = Boolean(
    isAdmin && (
      (!!incident?.is_exercise && incidentStatusLc === 'requested')
      || canCorrectRealIncidentReport(incident, isAdmin)
    )
  );
  const canEditReport = Boolean(
    isAdmin && (
      (!!incident?.is_exercise && incidentStatusLc === 'requested')
      || (isRealIncident && !['resolved', 'closed', 'cancelled', 'completed', 'false_alarm'].includes(incidentStatusLc))
    )
  );
  const [clsTypes, setClsTypes] = useState([]);
  const [clsEvents, setClsEvents] = useState([]);
  const [clsTriggers, setClsTriggers] = useState([]);
  const [scenarioPayload, setScenarioPayload] = useState(null);
  const [reportUsers, setReportUsers] = useState([]);
  const [selectedReporterId, setSelectedReporterId] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  useEffect(() => {
    if (canCorrectIncidentDefinition || !showCorrectionConfirm) return;
    setShowCorrectionConfirm(false);
    setError(correctionLockReason || 'This incident can no longer be corrected.');
  }, [canCorrectIncidentDefinition, correctionLockReason, showCorrectionConfirm]);

  useEffect(() => {
    if (formData.group !== 'report' || !canEditReport) return;
    (async () => {
      try {
        const res = await CommanServices.getAppUsers();
        if (res?.status) {
          const list = (res.data || []).map((u) => ({
            id: u.id || u._id,
            name: u.full_name || u.name || u.user_account || u.email,
            contact: u.email || u.phone || u.mobile || u.contact || u.user_account || '',
          }));
          setReportUsers(list);
        }
      } catch (e) { /* noop */ }
    })();
  }, [formData.group, canEditReport]);

  const reporterContactForUser = (uid) => {
    const u = reportUsers.find((x) => String(x.id) === String(uid));
    return u ? u.contact : '';
  };

  // Initialise reporter selection / anonymous flag from existing report data
  useEffect(() => {
    if (formData.group !== 'report' || !canEditReport) return;
    const rb = formData.reported_by || '';
    if (rb === 'Anonymous') { setIsAnonymous(true); return; }
    if (reportUsers.length && rb) {
      const match = reportUsers.find((u) => u.name === rb);
      if (match) setSelectedReporterId(match.id);
    }
  }, [reportUsers, canEditReport, formData.group]);

  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingAttachment(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > 50 * 1024 * 1024) { console.warn(`${file.name} is too large (max 50MB)`); continue; }
        const fd = new FormData();
        fd.append('files', file);
        const res = await CommanServices.uploadGenericFile(fd, 'incident_report');
        if (res?.status) {
          const data = Array.isArray(res.data) ? res.data : [res.data];
          data.forEach((m) => uploaded.push(m));
        }
      }
      if (uploaded.length) {
        handleInputChange('attachments', [...(formData.attachments || []), ...uploaded]);
      }
    } catch (err) {
      console.error('attachment upload failed', err);
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };
  const [typeId, setTypeId] = useState('');
  const [eventId, setEventId] = useState('none');
  const [triggerId, setTriggerId] = useState('none');
  const [locIds, setLocIds] = useState([]);

  useEffect(() => {
    if (formData.group !== 'report' || !canEditReport) return;
    setTypeId(incident?.type_id || '');
    setEventId(incident?.event_id || 'none');
    setTriggerId(incident?.trigger_id || 'none');
    setLocIds(Array.isArray(incident?.location) ? incident.location : []);
    (async () => {
      try {
        const res = await CommanServices.incidentClassifications();
        if (res.status) setClsTypes((res.data || []).map((e) => ({ ...e, id: e.id || e._id })));
      } catch (e) { /* noop */ }
    })();
  }, [formData.group, canEditReport, incident]);

  useEffect(() => {
    if (!canEditReport || !typeId) { setClsEvents([]); setClsTriggers([]); return; }
    (async () => {
      try {
        const res = await CommanServices.getincidentclassificationtypes(typeId);
        if (res.status) {
          setClsEvents((res.data.events || []).map((e) => ({ ...e, id: e.id || e._id })));
          setClsTriggers((res.data.triggers || []).map((e) => ({ ...e, id: e.id || e._id })));
        }
      } catch (e) { /* noop */ }
    })();
  }, [canEditReport, typeId]);

  const visibleTriggers = eventId === 'none'
    ? clsTriggers
    : clsTriggers.filter((t) => String(t.event_id) === String(eventId));

  // Auto-populate form data when modal opens
  useEffect(() => {
    if (editItem) {
      // Edit mode: populate from existing timeline item
      const meta = editItem.meta || {};
      const initialFormData = {
        group: editItem.group,
        title: editItem.title,
        details: editItem.details,
        chips: editItem.chips ? editItem.chips.join(', ') : '',
        actor_name: editItem.actor?.name || 'Current User',
        actor_type: editItem.actor?.type || 'user',
        // Report & Detection specific fields
        reported_at: meta.reportedAt || '',
        reported_by: meta.reportedBy || '',
        source_origin: meta.source?.origin || '',
        source_method: meta.source?.method || '',
        incident_type: meta.incidentType || '',
        event: meta.event || '',
        trigger: meta.trigger || '',
        location_override: meta.locationOverride || false,
        location_path: meta.locationPath || [],
        initial_description: meta.initialDescription || '',
        reporter_contact: meta.reporterContact || '',
        attachments: meta.attachments || []
      };
      setFormData(initialFormData);
      setOriginalData(initialFormData); // Store original for change detection
    } else if (activeGroup === 'report' && incident) {
      // Auto-populate Report & Detection fields from incident data
      const reporterName = incident.reporter_name || 'Anonymous';
      const reportedBy = `${reporterName} - App User`;
      
      console.log('🐛 DEBUG - Auto-populating Report & Detection from incident:', {
        incident_description: incident.description,
        incident_reporter_name: incident.reporter_name,
        incident_reporter_contact: incident.reporter_contact,
        incident_attachments: incident.attachments,
        formatted_reported_by: reportedBy
      });
      
      const initialFormData = {
        reported_at: incident.created_at || incident.detected_at || new Date().toISOString(),
        reported_by: reportedBy,
        source_origin: incident.source || 'App',
        source_method: incident.detection_method || 'Manual',
        incident_type: incident.incident_type || '',
        event: incident.event || '',
        trigger: incident.trigger || '',
        location_path: incident.location_names || incident.location || [],
        initial_description: incident.description || incident.title || '',
        reporter_contact: incident.reporter_contact || '',
        attachments: incident.attachments || [],
        title: 'Incident reported',
        details: incident.description || incident.title || 'Initial incident report',
        chips: 'Auto, Initial',
        actor_name: reportedBy
      };
      setFormData(prev => ({...prev, ...initialFormData}));
      setOriginalData(initialFormData); // Store original for change detection
    }
  }, [activeGroup, incident, editItem]);

  const generateAiDescription = async () => {
    try {
      setAiLoaderDescription(true);
      setAiError(null);

      // Validation: Type and Location are required
      const type = formData.incident_type || incident?.incident_type;
      const location = formData.location_path || incident?.location_names || incident?.location;

      if (!type || (Array.isArray(location) && location.length === 0) || !location) {
        setAiError("Please ensure Incident Type and Location are specified to generate a description.");
        setAiLoaderDescription(false);
        return;
      }

      // Get location path for context
      const locationLabel = Array.isArray(formData.location_path) 
        ? formData.location_path.join(' > ') 
        : (typeof formData.location_path === 'string' ? formData.location_path : '');

      const payload = {
        incident_type: formData.incident_type || incident?.incident_type,
        event: formData.event || incident?.event,
        trigger: formData.trigger || incident?.trigger,
        location: locationLabel,
        severity: incident?.severity || 'Medium',
        notes: formData.initial_description,
        attachments_count: formData.attachments?.length || 0
      }

      const res = await CommanServices.getAiDescription(payload);
      
      if (res?.status && res?.data?.description) {
        const aiText = res.data.description;
        
        if (formData.initial_description && formData.initial_description.trim() !== '') {
          setPendingAiText(aiText);
          setShowAiConfirm(true);
        } else {
          handleInputChange('initial_description', aiText);
        }
      } else {
        setAiError(res?.message || "Failed to generate AI description.");
      }
    } catch (error) {
      console.error('AI Generation Error:', error);
      setAiError("An error occurred during AI generation.");
    } finally {
      setAiLoaderDescription(false);
    }
  }

  const groupOptions = [
    { value: 'report', label: 'Report & Detection', icon: '🟢' },
    { value: 'acknowledgment', label: 'Acknowledgment', icon: '🟡' },
    { value: 'response_action', label: 'Response Actions', icon: '🟣' },
    { value: 'status_update', label: 'Status Updates', icon: '🟠' },
    { value: 'resolution', label: 'Resolution & Final Update', icon: '⚫' }
  ];

  // Get form configuration based on group
  const getFormConfig = (group) => {
    const configs = {
      report: {
        title: 'Report & Detection Entry',
        titlePlaceholder: 'Initial incident report',
        detailsPlaceholder: 'Describe what was observed, location details, and initial assessment...',
        specificFields: ['reported_by', 'source_method'],
        suggestedChips: ['Manual', 'Verified', 'Initial']
      },
      acknowledgment: {
        title: 'Acknowledgment Entry', 
        titlePlaceholder: 'Incident acknowledged by team',
        detailsPlaceholder: 'Acknowledgment notes and ETA information...',
        specificFields: ['eta_duration', 'notification_mode'],
        suggestedChips: ['Manual', 'Auto', 'SLA']
      },
      response_action: {
        title: 'Response Action Entry',
        titlePlaceholder: 'Action taken by response team',
        detailsPlaceholder: 'Detailed description of the action taken, steps performed, and results...',
        specificFields: ['action_type', 'result_outcome'],
        suggestedChips: ['Manual', 'Success', 'In Progress', 'Critical']
      },
      status_update: {
        title: 'Status Update Entry',
        titlePlaceholder: 'Situation status update',
        detailsPlaceholder: 'Current situation summary and any changes in conditions...',
        specificFields: ['risk_level_change', 'impacted_areas'],
        suggestedChips: ['Update', 'Risk Change', 'Monitoring']
      },
      resolution: {
        title: 'Resolution & Final Update Entry',
        titlePlaceholder: 'Incident resolution summary',
        detailsPlaceholder: 'Final resolution details and closure information...',
        specificFields: ['resolution_status', 'follow_up_required'],
        suggestedChips: ['Resolved', 'Follow-up', 'Complete']
      }
    };
    return configs[group] || configs.response_action;
  };

  const currentConfig = getFormConfig(formData.group);

  const handleSubmit = async (e, confirmedCorrectionReason = '') => {
    e?.preventDefault?.();
    const isEditMode = !!editItem;
    const correctionChanges = (
      isEditMode
      && formData.group === 'report'
      && isRealIncident
      && canCorrectIncidentDefinition
      && scenarioPayload
    ) ? buildIncidentDefinitionChanges(incident, scenarioPayload) : [];
    const normalizedCorrectionReason = String(confirmedCorrectionReason || '').trim();

    if (correctionChanges.length > 0) {
      if (!canCorrectRealIncidentReport(incident, isAdmin)) {
        setError(correctionLockReason || 'Classification and location are locked because incident operations have started.');
        return;
      }
      if (!normalizedCorrectionReason) {
        setError('');
        setPendingCorrectionChanges(correctionChanges);
        setCorrectionReason('');
        setShowCorrectionConfirm(true);
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      if (isEditMode) {
        // PATCH mode: only send changed fields
        const updates = {};
        const metaUpdates = {};
        const historyEntries = [];

        // Check for changes in Report & Detection fields
        if (formData.group === 'report') {
          // Track changes for history with structured logging
          if (originalData.location_path !== formData.location_path && 
              JSON.stringify(originalData.location_path) !== JSON.stringify(formData.location_path)) {
            metaUpdates.locationPath = formData.location_path;
            metaUpdates.locationOverride = true;
            
            // Format location display (join array or use as-is)
            const formatLocation = (loc) => {
              if (Array.isArray(loc)) {
                return loc.length > 0 ? loc.join(' – ') : 'Not specified';
              }
              return loc || 'Not specified';
            };
            
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'locationPath',
              action: 'override',
              change: 'Location overridden',
              from: formatLocation(originalData.location_path),
              to: formatLocation(formData.location_path),
              note: 'Location manually updated by user'
            });
          }

          if (originalData.reporter_contact !== formData.reporter_contact) {
            metaUpdates.reporterContact = formData.reporter_contact;
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'reporterContact',
              action: 'update',
              change: 'Reporter contact updated',
              from: originalData.reporter_contact || 'Not specified',
              to: formData.reporter_contact || 'Not specified'
            });
          }

          if (originalData.initial_description !== formData.initial_description) {
            metaUpdates.initialDescription = formData.initial_description;
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'initialDescription',
              action: 'edit',
              change: 'Initial description edited',
              from: originalData.initial_description || 'Not specified',
              to: formData.initial_description || 'Not specified'
            });
          }

          // Track other editable fields
          if (originalData.reported_at !== formData.reported_at) {
            metaUpdates.reportedAt = formData.reported_at;
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'reportedAt',
              action: 'update',
              change: 'Reported time updated',
              from: originalData.reported_at || 'Not specified',
              to: formData.reported_at || 'Not specified'
            });
          }

          if (originalData.reported_by !== formData.reported_by) {
            metaUpdates.reportedBy = formData.reported_by;
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'reportedBy',
              action: 'update',
              change: 'Reporter name updated',
              from: originalData.reported_by || 'Not specified',
              to: formData.reported_by || 'Not specified'
            });
          }

          if (originalData.incident_type !== formData.incident_type) {
            metaUpdates.incidentType = formData.incident_type;
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'incidentType',
              action: 'update',
              change: 'Incident type updated',
              from: originalData.incident_type || 'Not specified',
              to: formData.incident_type || 'Not specified'
            });
          }

          if (originalData.event !== formData.event) {
            metaUpdates.event = formData.event;
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'event',
              action: 'update',
              change: 'Event updated',
              from: originalData.event || 'Not specified',
              to: formData.event || 'Not specified'
            });
          }

          if (originalData.trigger !== formData.trigger) {
            metaUpdates.trigger = formData.trigger;
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'trigger',
              action: 'update',
              change: 'Trigger updated',
              from: originalData.trigger || 'Not specified',
              to: formData.trigger || 'Not specified'
            });
          }

          // Track attachments changes
          if (JSON.stringify(originalData.attachments) !== JSON.stringify(formData.attachments)) {
            metaUpdates.attachments = formData.attachments;
            historyEntries.push({
              ts: new Date().toISOString(),
              actor: formData.actor_name || 'Current User',
              field: 'attachments',
              action: 'update',
              change: 'Attachments updated',
              from: `${(originalData.attachments || []).length} file(s)`,
              to: `${(formData.attachments || []).length} file(s)`
            });
          }

          // Add history entries if there are changes
          if (historyEntries.length > 0) {
            metaUpdates.history = historyEntries;
          }
        }

        // Only include meta in updates if there are changes
        if (Object.keys(metaUpdates).length > 0) {
          updates.meta = metaUpdates;
        }

        // Exercise classification / location edits → PATCH incident (triggers backend timeline rebuild)
        const incPatch = {};
        let rebuildRelevantChanged = false;
        if (canEditReport && formData.group === 'report' && scenarioPayload) {
          const p = scenarioPayload;
          // Rebuild-relevant fields
          if (canCorrectIncidentDefinition) {
            if (String(p.type_id || '') !== String(incident?.type_id || '')) { incPatch.type_id = p.type_id || ''; rebuildRelevantChanged = true; }
            if (String(p.event_id || '') !== String(incident?.event_id || '')) { incPatch.event_id = p.event_id || ''; rebuildRelevantChanged = true; }
            if (String(p.trigger_id || '') !== String(incident?.trigger_id || '')) { incPatch.trigger_id = p.trigger_id || ''; rebuildRelevantChanged = true; }
            const curLoc = Array.isArray(incident?.location) ? incident.location : [];
            if (JSON.stringify(p.location || []) !== JSON.stringify(curLoc) || !!p.is_unknown_location !== !!incident?.is_unknown_location) {
              incPatch.location = p.location || [];
              incPatch.is_unknown_location = !!p.is_unknown_location;
              rebuildRelevantChanged = true;
            }
            if (p.severity && String(p.severity) !== String(incident?.severity || '')) incPatch.severity = p.severity;
          }
          // Non-rebuild fields (just saved)
          if (String(p.exercise_classification || '') !== String(incident?.exercise_classification || '')) incPatch.exercise_classification = p.exercise_classification || '';
          if (JSON.stringify(p.impacted_departments || []) !== JSON.stringify(incident?.impacted_departments || [])) incPatch.impacted_departments = p.impacted_departments || [];
          if (correctionChanges.length > 0) {
            incPatch.correction_reason = normalizedCorrectionReason;
            incPatch.correction_expected_revision = Number(incident?.definition_revision || 0);
          }
        }

        // Reporter fields → persist on the incident so the report card reflects them after rebuild.
        if (canEditReport && formData.group === 'report') {
          if ((formData.reported_by || '') !== (originalData.reported_by || '')) incPatch.reporter_name = formData.reported_by || '';
          if ((formData.reporter_contact || '') !== (originalData.reporter_contact || '')) incPatch.reporter_contact = formData.reporter_contact || '';
          if (formData.reported_by_id !== undefined) incPatch.reported_by_id = formData.reported_by_id || '';
        }

        // Only send PATCH if there are actual changes
        if (Object.keys(updates).length === 0 && Object.keys(incPatch).length === 0) {
          setError('No changes detected');
          setLoading(false);
          return;
        }

        // PATCH request with ETag
        const headers = {
          'Content-Type': 'application/json',
        };
        if (editItem.etag) {
          headers['If-Match'] = editItem.etag;
        }

        let updatedItem = editItem;
        let incidentPatchApplied = false;
        // Apply the protected incident correction before timeline metadata so a
        // lifecycle/race rejection cannot leave a partially edited report card.
        if (correctionChanges.length > 0 && Object.keys(incPatch).length > 0) {
          try {
            const correctionResponse = await CommanServices.updatePatchIncidents(incident.id, incPatch);
            if (correctionResponse?.status === false) {
              throw new Error(correctionResponse?.message || correctionResponse?.data?.detail || 'Incident correction was rejected.');
            }
            incidentPatchApplied = true;
          } catch (error) {
            console.error('Failed to correct incident classification/location:', error);
            setError(error?.response?.data?.detail || error?.message || 'Failed to correct incident classification/location.');
            setLoading(false);
            return;
          }
        }
        if (Object.keys(updates).length > 0) {
          const response = await CommanServices.updateIncidentTimeline(incident.id, editItem.id, updates);
          if (response.status) {
            updatedItem = await response.data;
            if (metaUpdates.locationPath) {
              try {
                const data = { location_names: Array.isArray(metaUpdates.locationPath)
                                  ? metaUpdates.locationPath
                                  : [metaUpdates.locationPath] };
                await CommanServices.updatePatchIncidents(incident.id, data);
              } catch (error) {
                console.error('Failed to sync incident location:', error);
              }
            }
          } else if (response.status === 412) {
            setError('This item was modified by another user. Please refresh and try again.');
            setLoading(false);
            return;
          } else {
            const errorData = await response.json();
            setError(errorData.detail || 'Failed to update timeline entry');
            setLoading(false);
            return;
          }
        }

        // Apply classification / location change to the incident (rebuilds the exercise plan)
        if (!incidentPatchApplied && Object.keys(incPatch).length > 0) {
          try {
            const patchResponse = await CommanServices.updatePatchIncidents(incident.id, incPatch);
            if (patchResponse?.status === false) {
              throw new Error(patchResponse?.message || patchResponse?.data?.detail || 'Incident update was rejected.');
            }
          } catch (error) {
            console.error('Failed to update incident classification/location:', error);
            setError(error?.response?.data?.detail || error?.message || 'Failed to update classification/location.');
            setLoading(false);
            return;
          }
        }

        setShowCorrectionConfirm(false);
        setPendingCorrectionChanges([]);
        setCorrectionReason('');
        onSuccess(updatedItem); // Pass updated item for UI reconciliation
        // Report edits are fully handled via the timeline meta PATCH + incident PATCH
        // above (backend re-syncs report meta). Stop here so the stale formData
        // classification values don't overwrite the freshly-synced meta below.
        if (formData.group === 'report') {
          setLoading(false);
          return;
        }
        // POST mode: create new entry (or upsert for report group)
        const entryData = {
          incident_id: incident.id,
          group: formData.group,
          title: formData.title,
          details: formData.details,
          chips: formData.chips ? formData.chips.split(',').map(c => c.trim()).filter(c => c) : [],
          actor: {
            id: 'user_current',
            name: formData.actor_name || 'Current User',
            type: formData.actor_type
          },
          meta: {}
        };

        // Add Report & Detection specific data to meta
        if (formData.group === 'report') {
          entryData.meta = {
            reportedAt: formData.reported_at,
            reportedBy: formData.reported_by,
            source: {
              origin: formData.source_origin,
              method: formData.source_method
            },
            incidentType: formData.incident_type,
            event: formData.event,
            trigger: formData.trigger,
            locationPath: formData.location_path,
            locationOverride: formData.location_override,
            initialDescription: formData.initial_description,
            reporterContact: formData.reporter_contact,
            attachments: formData.attachments,
            origin: 'auto',
            deterministic_id: `itm:${incident.id}:report:auto`
          };
        }

        // Use upsert endpoint for report group to prevent duplicates
        if (formData.group === 'report') {
          const response = await CommanServices.incidentTimelineUpsertReport(incident.id, entryData);
          if (response.status) {
            const createdItem = await response.data;
            onSuccess(createdItem);
          } else {
            const errorData = await response.data;
            setError(errorData.message || 'Failed to create timeline entry');
          }
        }else{
          const response = await CommanServices.addincidentTimeline(incident.id, entryData);
          if (response.status) {
            const createdItem = await response.data;
            onSuccess(createdItem);
          } else {
            const errorData = await response.data;
            setError(errorData.message || 'Failed to create timeline entry');
          }
        }

      }
    } catch (error) {
      console.error('Error saving timeline entry:', error);
      setError(error?.response?.data?.detail || error?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isEditMode = !!editItem;
  const modalTitle = isEditMode && formData.group === 'report' 
    ? 'Report & Detection Preview' 
    : isEditMode 
      ? `Edit ${currentConfig.title}` 
      : currentConfig.title;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">{modalTitle}</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Show group badge for Report & Detection, full form for others */}
          {formData.group === 'report' ? (
            <>{/* Report & Detection badge removed */}</>
          ) : (
            <>
              {/* Group Selection - Only show for non-report groups */}
              {!activeGroup && (
                <div className="space-y-2">
                  <Label htmlFor="group" className="text-sm font-medium text-gray-700">
                    Timeline Group *
                  </Label>
                  <Select value={formData.group} onValueChange={(value) => handleInputChange('group', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timeline group" />
                    </SelectTrigger>
                    <SelectContent>
                      {groupOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center space-x-2">
                            <span>{option.icon}</span>
                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Group Badge - Show when activeGroup is provided (non-report) */}
              {activeGroup && activeGroup !== 'report' && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                  <span className="text-lg">{groupOptions.find(g => g.value === activeGroup)?.icon}</span>
                  <span className="font-medium text-blue-900">
                    {groupOptions.find(g => g.value === activeGroup)?.label}
                  </span>
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                  Entry Title *
                </Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={currentConfig.titlePlaceholder}
                  className="w-full"
                  required
                />
              </div>

              {/* Details */}
              <div className="space-y-2">
                <Label htmlFor="details" className="text-sm font-medium text-gray-700">
                  Details *
                </Label>
                <textarea
                  id="details"
                  value={formData.details}
                  onChange={(e) => handleInputChange('details', e.target.value)}
                  placeholder={currentConfig.detailsPlaceholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] resize-y"
                  required
                />
              </div>

              {/* Actor Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="actor_name" className="text-sm font-medium text-gray-700">
                    Actor Name *
                  </Label>
                  <Input
                    id="actor_name"
                    type="text"
                    value={formData.actor_name}
                    onChange={(e) => handleInputChange('actor_name', e.target.value)}
                    placeholder="e.g., Emergency Response Team"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="actor_type" className="text-sm font-medium text-gray-700">
                    Actor Type
                  </Label>
                  <Select value={formData.actor_type} onValueChange={(value) => handleInputChange('actor_type', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Report & Detection Specific Fields */}
          {formData.group === 'report' && (
            <>
              {/* Reported At — exercises capture it on activation; real
                  incidents already have the actual report time (read-only) */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Reported At</Label>
                {canEditReport && incident?.is_exercise ? (
                  <>
                    <Input
                      value=""
                      disabled
                      readOnly
                      data-testid="report-reported-at"
                      placeholder="Captured automatically on activation"
                      className="bg-gray-100 text-gray-400 cursor-not-allowed"
                    />
                    <p className="text-[11px] text-gray-500">This will be captured automatically the moment the exercise is activated.</p>
                  </>
                ) : (
                  <Input
                    type="datetime-local"
                    value={formData.reported_at ? new Date(formData.reported_at).toISOString().slice(0, 16) : ''}
                    readOnly
                    data-testid="report-reported-at"
                    className="bg-gray-50 text-gray-600"
                  />
                )}
              </div>

              {/* Reported By */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Reported By</Label>
                {canEditReport ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      {isAnonymous ? (
                        <Input value="Anonymous" readOnly data-testid="report-reported-by" className="bg-amber-50 text-amber-700 font-medium" />
                      ) : (
                        <Select
                          value={selectedReporterId || ''}
                          onValueChange={(v) => {
                            setSelectedReporterId(v);
                            const u = reportUsers.find((x) => String(x.id) === String(v));
                            handleInputChange('reported_by', u ? u.name : '');
                            handleInputChange('reporter_contact', u ? u.contact : '');
                            handleInputChange('actor_name', u ? u.name : '');
                            handleInputChange('reported_by_id', u ? u.id : '');
                          }}
                        >
                          <SelectTrigger data-testid="report-reported-by" className="text-sm"><SelectValue placeholder="Select the user who reported" /></SelectTrigger>
                          <SelectContent className="max-h-72 overflow-y-auto">
                            {reportUsers.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant={isAnonymous ? 'default' : 'outline'}
                      data-testid="report-anonymous-btn"
                      className={isAnonymous ? 'bg-amber-600 hover:bg-amber-700 text-white h-9 text-xs' : 'h-9 text-xs'}
                      onClick={() => {
                        const next = !isAnonymous;
                        setIsAnonymous(next);
                        if (next) {
                          setSelectedReporterId('');
                          handleInputChange('reported_by', 'Anonymous');
                          handleInputChange('reporter_contact', '');
                          handleInputChange('actor_name', 'Anonymous');
                          handleInputChange('reported_by_id', '');
                        } else {
                          handleInputChange('reported_by', '');
                          handleInputChange('reporter_contact', '');
                          handleInputChange('actor_name', '');
                          handleInputChange('reported_by_id', '');
                        }
                      }}
                    >
                      {isAnonymous ? 'Anonymous ✓' : 'Anonymous'}
                    </Button>
                  </div>
                ) : (
                  <Input value={formData.reported_by} readOnly data-testid="report-reported-by" className="bg-gray-50 text-gray-600" />
                )}
              </div>

              {/* Reporter Contact */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Reporter Contact</Label>
                <Input
                  value={formData.reporter_contact}
                  onChange={(e) => canEditReport && isAnonymous && handleInputChange('reporter_contact', e.target.value)}
                  readOnly={!canEditReport || !isAnonymous}
                  data-testid="report-reporter-contact"
                  className={(canEditReport && isAnonymous) ? '' : 'bg-gray-50 text-gray-600'}
                  placeholder={isAnonymous ? 'Enter contact manually' : 'Pulled from the selected user profile'}
                />
              </div>

              {/* Incident Classification + Severity + Location + Impacted Departments (edit mode) */}
              {isAdmin && isRealIncident && correctionLockReason && (
                <div className="md:col-span-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="incident-correction-locked">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-bold">Classification and location are locked</p>
                    <p className="mt-0.5 text-xs text-amber-800">{correctionLockReason}</p>
                  </div>
                </div>
              )}
              {canEditReport ? (
                <div className="md:col-span-2 p-4 bg-indigo-50/40 border border-indigo-100 rounded-xl">
                  <ExerciseScenarioEditor
                    incident={incident}
                    onChange={setScenarioPayload}
                    showExerciseFields={!!incident?.is_exercise}
                    lockIncidentDefinition={!canCorrectIncidentDefinition}
                  />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Incident Type</Label>
                      <Input value={formData.incident_type} readOnly className="bg-gray-50 text-gray-600 text-sm" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Event</Label>
                      <Input value={(!formData.event || formData.event.trim() === '' || formData.event === 'All') ? 'Unspecified' : formData.event} readOnly className="bg-gray-50 text-gray-600 text-sm" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Trigger</Label>
                      <Input value={(!formData.trigger || formData.trigger.trim() === '' || formData.trigger === 'All') ? 'Unspecified' : formData.trigger} readOnly className="bg-gray-50 text-gray-600 text-sm" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Location</Label>
                    <Input
                      value={
                        incident?.location_names && Array.isArray(incident.location_names) && incident.location_names.length > 0
                          ? incident.location_names.join(' > ')
                          : Array.isArray(formData.location_path) ? formData.location_path.join(' > ') : formData.location_path
                      }
                      readOnly
                      className="bg-gray-50 text-gray-600"
                    />
                  </div>
                </>
              )}

              {/* Initial Description - Read-only */}
              <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Brain className="h-4 w-4 text-violet-500 animate-pulse" />
                  <Label htmlFor="initial_description" className="text-sm font-semibold bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-transparent">
                    Initial Description
                  </Label>
                </div>
                {
                  aiLoaderDescription ? (
                    <div className="flex items-center space-x-1.5 px-2 py-1 bg-violet-100 rounded-full animate-pulse border border-violet-200">
                      <Loader2 className="w-3 h-3 text-violet-600 animate-spin" />
                      <span className="text-[10px] font-bold text-violet-700 uppercase">Generating...</span>
                    </div>
                  ) : (
                    <button 
                      type="button"
                      className="group flex items-center space-x-1.5 px-2 py-1 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-full transition-all active:scale-95 shadow-sm"
                      onClick={generateAiDescription}
                    >
                      <Sparkles className="w-3 h-3 text-violet-600 group-hover:rotate-12 transition-transform" />
                      <span className="text-[10px] font-bold text-violet-700 uppercase">Generate with AI</span>
                    </button>
                  )
                }
              </div>
              
              {aiError && (
                <div className="mb-2 p-2 bg-red-50 border border-red-100 rounded-lg flex items-center justify-between animate-in fade-in slide-in-from-top-1">
                  <p className="text-[10px] text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {aiError}
                  </p>
                  <button 
                    type="button"
                    onClick={generateAiDescription}
                    className="text-[10px] font-bold text-red-700 hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              <textarea
                id="initial_description"
                value={formData.initial_description}
                onChange={(e) => handleInputChange('initial_description', e.target.value)}
                placeholder="Describe what happened, when it occurred, and any immediate actions taken..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-h-[120px] resize-y transition-all"
              />
              <p className="text-[10px] text-slate-500 mt-1 italic">
                💡 AI will use current type, event, trigger, and location to generate a summary.
              </p>
            </div>

              {/* Attachments - Read-only with Preview */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Attachments</Label>
                <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg">
                  {formData.attachments.length > 0 ? (
                    <div className="space-y-2">
                      {formData.attachments.map((attachment, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => setPreviewAttachment(attachment)}
                        >
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">📎</span>
                            <span className="text-gray-700">{attachment.name || 'Attachment ' + (index + 1)}</span>
                            {attachment.size && (
                              <span className="text-gray-400 text-xs">({(attachment.size / 1024).toFixed(1)} KB)</span>
                            )}
                          </div>
                          <span className="text-blue-600 text-xs">Click to preview</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-gray-500">No attachments from original report</p>
                    </div>
                  )}
                  {canEditReport && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label
                        htmlFor="report-attachment-input"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold cursor-pointer"
                        data-testid="report-add-attachment-btn"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {uploadingAttachment ? 'Uploading…' : 'Add attachments (photo / PDF / video)'}
                      </label>
                      <input
                        id="report-attachment-input"
                        type="file"
                        multiple
                        accept="image/*,application/pdf,video/*"
                        className="hidden"
                        disabled={uploadingAttachment}
                        onChange={handleAttachmentUpload}
                        data-testid="report-attachment-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Other Group-Specific Fields */}
          {currentConfig.specificFields.includes('source_method') && formData.group !== 'report' && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Detection Method</Label>
              <Select value={formData.source_method} onValueChange={(value) => handleInputChange('source_method', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="How was this incident detected?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Manual">Manual Report</SelectItem>
                  <SelectItem value="IoT Sensor">IoT Sensor</SelectItem>
                  <SelectItem value="Call">Phone Call</SelectItem>
                  <SelectItem value="CCTV">CCTV Monitoring</SelectItem>
                  <SelectItem value="Auto Alert">Automatic Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {currentConfig.specificFields.includes('eta_duration') && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">ETA (Estimated Time to Arrival)</Label>
              <Select value={formData.eta_duration} onValueChange={(value) => handleInputChange('eta_duration', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Expected arrival time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5 min">5 minutes</SelectItem>
                  <SelectItem value="15 min">15 minutes</SelectItem>
                  <SelectItem value="30 min">30 minutes</SelectItem>
                  <SelectItem value="1 hour">1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {currentConfig.specificFields.includes('action_type') && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Action Type</Label>
              <Select value={formData.action_type} onValueChange={(value) => handleInputChange('action_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Type of action taken" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Containment">Containment</SelectItem>
                  <SelectItem value="Mitigation">Mitigation</SelectItem>
                  <SelectItem value="Evacuation">Evacuation</SelectItem>
                  <SelectItem value="Communication">Communication</SelectItem>
                  <SelectItem value="Recovery">Recovery</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {currentConfig.specificFields.includes('result_outcome') && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Result/Outcome</Label>
              <Select value={formData.result_outcome} onValueChange={(value) => handleInputChange('result_outcome', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Action result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Success">Success</SelectItem>
                  <SelectItem value="Partial">Partial Success</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {currentConfig.specificFields.includes('resolution_status') && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Resolution Status</Label>
              <Select value={formData.resolution_status} onValueChange={(value) => handleInputChange('resolution_status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Final resolution status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Contained">Contained</SelectItem>
                  <SelectItem value="Escalated Externally">Escalated Externally</SelectItem>
                  <SelectItem value="Unresolved">Unresolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {currentConfig.specificFields.includes('follow_up_required') && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Follow-up Required</Label>
              <Select value={formData.follow_up_required.toString()} onValueChange={(value) => handleInputChange('follow_up_required', value === 'true')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">No</SelectItem>
                  <SelectItem value="true">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Chips/Tags - Only for non-report groups */}
          {formData.group !== 'report' && (
            <div className="space-y-2">
              <Label htmlFor="chips" className="text-sm font-medium text-gray-700">
                Tags/Chips
              </Label>
              <Input
                id="chips"
                type="text"
                value={formData.chips}
                onChange={(e) => handleInputChange('chips', e.target.value)}
                placeholder={currentConfig.suggestedChips.join(', ') + ' (comma-separated)'}
              />
              <p className="text-xs text-gray-500">Suggested: {currentConfig.suggestedChips.join(', ')}</p>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Action Buttons — report edit mode (exercise, Requested stage) */}
          {isEditMode && formData.group === 'report' && canEditReport && (
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={onClose} className="px-4 py-2" data-testid="report-cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="report-save-btn"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
          {/* <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                loading || 
                !formData.group || 
                (formData.group === 'report' ? 
                  !formData.initial_description : 
                  (!formData.title || !formData.details || !formData.actor_name)
                )
              }
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Saving...' : (formData.group === 'report' ? 'Save Changes' : 'Add Entry')}
            </Button>
          </div> */}
        </form>
      </div>

      {/* Attachment Preview Modal */}
      {previewAttachment && (
        <AttachmentPreviewModal 
          attachment={previewAttachment} 
          onClose={() => setPreviewAttachment(null)} 
        />
      )}

      <AlertDialog
        open={showCorrectionConfirm}
        onOpenChange={(open) => {
          if (loading) return;
          setShowCorrectionConfirm(open);
          if (!open) {
            setPendingCorrectionChanges([]);
            setCorrectionReason('');
          }
        }}
      >
        <AlertDialogContent className="max-w-xl" data-testid="incident-correction-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-900">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Confirm incident correction
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will re-evaluate scenario matching and the pending response plan. Your administrator identity, the old and new values, and the reason will be permanently recorded in the incident audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-56 space-y-2 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
            {pendingCorrectionChanges.map((change) => (
              <div key={change.field} className="grid grid-cols-[130px_minmax(0,1fr)] gap-3 text-xs" data-testid={`incident-correction-change-${change.field}`}>
                <span className="font-bold text-slate-700">{change.label}</span>
                <span className="min-w-0 text-slate-600">
                  <span className="break-words line-through decoration-slate-400">{change.oldValue}</span>
                  <span className="mx-2 font-bold text-amber-600">→</span>
                  <span className="break-words font-semibold text-slate-900">{change.newValue}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="incident-correction-reason" className="text-sm font-bold text-slate-800">
              Reason for correction <span className="text-red-500">*</span>
            </Label>
            <textarea
              id="incident-correction-reason"
              data-testid="incident-correction-reason"
              value={correctionReason}
              onChange={(event) => setCorrectionReason(event.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Explain why the originally reported classification or location is incorrect."
              className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
            />
            <p className="text-right text-[10px] text-slate-400">{correctionReason.length}/500</p>
          </div>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>}

          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={() => {
                setShowCorrectionConfirm(false);
                setPendingCorrectionChanges([]);
                setCorrectionReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="confirm-incident-correction"
              disabled={loading || !correctionReason.trim()}
              onClick={() => handleSubmit(null, correctionReason)}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {loading ? 'Saving correction…' : 'Confirm correction'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showAiConfirm} onOpenChange={setShowAiConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="relative">
            <button
              type="button"
              onClick={() => setShowAiConfirm(false)}
              className="absolute right-0 top-0 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <AlertDialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-violet-500" />
              AI Description Ready
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              An incident description already exists. Would you like to overwrite it with the new AI version or append it to the end?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex sm:flex-row gap-2">
            <button
              type="button"
              className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
              onClick={() => {
                handleInputChange('initial_description', pendingAiText);
                setShowAiConfirm(false);
              }}
            >
              Overwrite
            </button>
            <button
              type="button"
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-md transition-colors shadow-sm"
              onClick={() => {
                handleInputChange('initial_description', formData.initial_description + "\n\n" + pendingAiText);
                setShowAiConfirm(false);
              }}
            >
              Append
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TimelineEntryModal;
